import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  ExamByIdDetailsResponse,
  ExamDetailsResponse,
  ExamState,
  ExamSubjectDetailsResponse,
  ExamSubjectDetails,
  PublishExamResponse,
  CompleteExamResponse,
  CancelExamResponse,
} from '../../interface/examDetails.interface';
import {
  fetchExams,
  fetchExamById,
  createExam,
  updateExamAction,
  fetchExamSubjectById,
  publishExam,
  releaseExam,
  cancelExam,
  fetchExamAcknowledgements,
} from '../action/exam.action';

const initialState: ExamState = {
  exams: [],
  selectedExam: undefined,
  isLoading: false,
  isViewLoading: false,
  total: 0,
  error: undefined,
  selectedExamSubject: undefined,
  isExamSubjectLoading: false,
  acknowledgements: [],
  acknowledgementsTotal: 0,
  isLoadingAcknowledgements: false,
  acknowledgementsError: undefined,
};

const examSlice = createSlice({
  name: 'exam',
  initialState,
  reducers: {
    resetSelectedExam(state) {
      state.selectedExam = undefined;
      state.selectedExamSubject = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExams.pending, (state) => {
        state.isViewLoading = true;
        state.total = 0;
        state.exams = [];
        state.error = undefined;
      })
      .addCase(fetchExams.fulfilled, (state, action: PayloadAction<ExamDetailsResponse>) => {
        state.isViewLoading = false;
        state.exams = action.payload.data.exams;
        state.total = action.payload.data.total;
        state.error = undefined;
      })
      .addCase(fetchExams.rejected, (state, action) => {
        state.isViewLoading = false;
        state.exams = [];
        state.total = 0;
        state.error = action.payload as string;
      })
      .addCase(fetchExamById.pending, (state) => {
        state.isLoading = true;
        state.selectedExam = undefined;
        state.error = undefined;
      })
      .addCase(fetchExamById.fulfilled, (state, action: PayloadAction<ExamByIdDetailsResponse>) => {
        state.isLoading = false;
        state.selectedExam = action.payload.data.exam;
        state.error = undefined;
      })
      .addCase(fetchExamById.rejected, (state, action) => {
        state.isLoading = false;
        state.selectedExam = undefined;
        state.error = action.payload as string;
      })
      .addCase(createExam.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(createExam.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally, push the new exam to exams array or refetch list
      })
      .addCase(createExam.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_create_exam', 'Failed to create exam.');
      })
      .addCase(updateExamAction.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(updateExamAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = undefined;
      })
      .addCase(updateExamAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message || i18n.t('toast.failed_to_update_exam', 'Failed to update exam');
      })
      .addCase(fetchExamSubjectById.pending, (state) => {
        state.isExamSubjectLoading = true;
        state.selectedExamSubject = undefined;
      })
      .addCase(
        fetchExamSubjectById.fulfilled,
        (state, action: PayloadAction<ExamSubjectDetailsResponse>) => {
          state.isExamSubjectLoading = false;
          state.selectedExamSubject = action.payload.data;
        },
      )
      .addCase(fetchExamSubjectById.rejected, (state, action) => {
        state.isExamSubjectLoading = false;
        state.selectedExamSubject = undefined;
      })
      .addCase(publishExam.pending, (state) => {
        state.isExamSubjectLoading = true;
      })
      .addCase(publishExam.fulfilled, (state, action: PayloadAction<PublishExamResponse>) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(publishExam.rejected, (state, action) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(releaseExam.pending, (state) => {
        state.isExamSubjectLoading = true;
      })
      .addCase(releaseExam.fulfilled, (state, action: PayloadAction<CompleteExamResponse>) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(releaseExam.rejected, (state, action) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(cancelExam.pending, (state) => {
        state.isExamSubjectLoading = true;
      })
      .addCase(cancelExam.fulfilled, (state) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(cancelExam.rejected, (state, action) => {
        state.isExamSubjectLoading = false;
      })
      .addCase(fetchExamAcknowledgements.pending, (state) => {
        state.isLoadingAcknowledgements = true;
        state.acknowledgementsError = undefined;
      })
      .addCase(fetchExamAcknowledgements.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoadingAcknowledgements = false;
        state.acknowledgements = action.payload.data.acknowledgements;
        state.acknowledgementsTotal = action.payload.data.total;
        state.acknowledgementsError = undefined;
      })
      .addCase(fetchExamAcknowledgements.rejected, (state, action) => {
        state.isLoadingAcknowledgements = false;
        state.acknowledgements = [];
        state.acknowledgementsTotal = 0;
        state.acknowledgementsError =
          action.payload?.message || i18n.t('toast.failed_to_fetch_acknowledgements', 'Failed to fetch acknowledgements.');
      });
  },
});

export const { resetSelectedExam } = examSlice.actions;
export default examSlice.reducer;
